/**
 * Player Navigation Component
 * 
 * @description
 * Navigation component for Player-facing interface.
 * Features a top navigation bar with wallet balance display and quick actions.
 * 
 * @module components/navigation/PlayerNav
 */

import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface PlayerNavItem {
  /** Display label */
  label: string;
  /** Route path */
  to: string;
  /** Icon component */
  icon?: React.ReactNode;
}

export interface PlayerNavProps {
  /** Navigation items */
  items: PlayerNavItem[];
  /** Player name */
  playerName?: string;
  /** Crypto balance to display */
  balance?: {
    amount: string;
    currency: string;
  };
  /** Notification count */
  notifications?: number;
  /** Additional class */
  className?: string;
}

/**
 * Player Navigation Component
 * 
 * @example
 * ```tsx
 * <PlayerNav
 *   items={[
 *     { label: "Dashboard", to: "/player/dashboard", icon: <HomeIcon /> },
 *     { label: "Wallet", to: "/player/wallet", icon: <WalletIcon /> },
 *   ]}
 *   playerName="Player123"
 *   balance={{ amount: "1,250.00", currency: "USDT" }}
 * />
 * ```
 */
export function PlayerNav({
  items,
  playerName = 'Player',
  balance,
  notifications = 0,
  className,
}: PlayerNavProps): React.ReactElement {
  return (
    <header
      className={cn(
        'flex items-center justify-between h-16 px-4',
        'bg-background-secondary border-b border-border',
        'sticky top-0 z-40',
        className
      )}
    >
      /**
       * Logo Section
       */
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-gold to-gold-dark">
          <span className="text-sm font-bold text-background font-display">CS</span>
        </div>
        <span className="font-display font-semibold text-foreground tracking-wide hidden sm:block">
          Casino
        </span>
      </div>

      /**
       * Center Navigation
       */
      <nav className="hidden md:flex items-center gap-1">
        {items.map((item) => (
          <PlayerNavItem key={item.to} item={item} />
        ))}
      </nav>

      /**
       * Right Section - Balance & Profile
       */
      <div className="flex items-center gap-4">
        /**
         * Balance Display
         */
        {balance && (
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background-tertiary border border-border">
            <span className="text-gold">
              <WalletIcon />
            </span>
            <span className="text-sm font-medium text-foreground">
              {balance.amount}
            </span>
            <span className="text-xs text-foreground-muted">
              {balance.currency}
            </span>
          </div>
        )}

        /**
         * Notifications
         */
        <button
          className={cn(
            'relative p-2 rounded-lg text-foreground-muted',
            'hover:bg-background-tertiary hover:text-foreground',
            'transition-base focus-ring'
          )}
        >
          <BellIcon />
          {notifications > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-error text-white">
              {notifications > 9 ? '9+' : notifications}
            </span>
          )}
        </button>

        /**
         * Player Profile Dropdown
         */
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-background-tertiary cursor-pointer transition-base">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-neon-cyan to-neon-magenta">
            <span className="text-sm font-bold text-background">
              {playerName.charAt(0).toUpperCase()}
            </span>
          </div>
          <span className="text-sm font-medium text-foreground hidden sm:block">
            {playerName}
          </span>
          <ChevronDownIcon />
        </div>
      </div>
    </header>
  );
}

/**
 * Individual Navigation Item
 */
function PlayerNavItem({ item }: { item: PlayerNavItem }): React.ReactElement {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          'nav-item',
          isActive && 'active'
        )
      }
    >
      {item.icon && <span className="w-5 h-5">{item.icon}</span>}
      <span>{item.label}</span>
    </NavLink>
  );
}

PlayerNav.displayName = 'PlayerNav';

/**
 * Icon Components
 */

export function BellIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

export function ChevronDownIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

export function WalletIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  );
}

export function HomeIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

export function BettingIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
      />
    </svg>
  );
}

export function HistoryIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

export function UsersIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

export function SearchIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

export function GiftIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5a2 2 0 10-2 2h-2a2 2 0 00-2-2v3a2 2 0 012 2zm0 0v1a2 2 0 104 0m0 0v1a2 2 0 11-4 0m4 0V6a2 2 0 012-2h2a2 2 0 012 2v3a2 2 0 01-2 2h-2zm0 0V4a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2z"
      />
    </svg>
  );
}

export function TrophyIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

export function SparklesIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

export function EditIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}